'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { getProjectBilling } from '@/lib/api'
import type { BillingInfo, ProjectBillingInfo } from '@/lib/api'
import { useBillingInfoStore } from '@/store/billingInfoStore'
import { useAuth } from './useAuth'

interface UseBillingInfoResult {
  /** User's billing info (personal org + team orgs) */
  billingInfo: BillingInfo | null
  /** Whether billing data is loading */
  isLoading: boolean
  /** Error if billing fetch failed */
  error: Error | null
  /** Refresh billing info from server */
  refresh: () => Promise<void>
}

/**
 * Hook to fetch and cache user's billing information.
 * Backed by a shared store so concurrent consumers share one network request.
 * Automatically refreshes on window focus and when auth state changes.
 */
export function useBillingInfo(): UseBillingInfoResult {
  const { isAnonymous, isLoading: authLoading, userId } = useAuth()
  const billingInfo = useBillingInfoStore(s => s.billingInfo)
  const isLoading = useBillingInfoStore(s => s.isLoading)
  const error = useBillingInfoStore(s => s.error)
  const fetch = useBillingInfoStore(s => s.fetch)
  const forceFetch = useBillingInfoStore(s => s.forceFetch)
  const clear = useBillingInfoStore(s => s.clear)
  const prevIsAnonymous = useRef(isAnonymous)
  const prevUserId = useRef(userId)

  useEffect(() => {
    const userChanged = prevUserId.current !== userId
    prevUserId.current = userId
    if (userChanged || isAnonymous) clear()
  }, [isAnonymous, userId, clear])

  // Initial fetch (skip if auth is still loading or user is anonymous)
  useEffect(() => {
    if (authLoading) return
    if (isAnonymous) return
    fetch()
  }, [authLoading, isAnonymous, fetch])

  // Re-fetch when user transitions from anonymous to real account
  useEffect(() => {
    if (prevIsAnonymous.current && !isAnonymous && !authLoading) {
      console.log('[BillingInfo] Auth changed from anonymous to real - refreshing billing')
      useBillingInfoStore.setState({ isLoading: true })
      forceFetch()
    }
    prevIsAnonymous.current = isAnonymous
  }, [isAnonymous, authLoading, forceFetch])

  // Refresh on window focus (skip for anonymous users)
  useEffect(() => {
    const handleFocus = () => {
      if (!isAnonymous) fetch()
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [fetch, isAnonymous])

  return {
    billingInfo: isAnonymous ? null : billingInfo,
    isLoading,
    error,
    // Explicit refresh (post-mutation) bypasses dedupe so callers always get
    // data captured after their mutation completed.
    refresh: forceFetch,
  }
}

interface UseProjectBillingResult {
  /** Project's billing configuration and balance */
  projectBilling: ProjectBillingInfo | null
  /** Whether billing data is loading */
  isLoading: boolean
  /** Error if billing fetch failed */
  error: Error | null
  /** Refresh project billing from server */
  refresh: () => Promise<void>
}

// Module-level cache so panel remounts render billing instantly (the cost row
// and insufficient-balance warnings otherwise wait a full roundtrip on every
// mount). Served stale, revalidated by the background fetch. Cleared whenever
// the authenticated user changes so a sign-out/sign-in in the same tab can
// never serve the previous user's balances.
const projectBillingCache = new Map<string, ProjectBillingInfo>()
let projectBillingCacheUserId: string | null = null

function ensureCacheOwnedBy(userId: string | null): void {
  if (projectBillingCacheUserId !== userId) {
    projectBillingCache.clear()
    projectBillingCacheUserId = userId
  }
}

/**
 * Hook to fetch project-specific billing info.
 * Returns which org pays and the available balance.
 */
export function useProjectBilling(projectId: string | null): UseProjectBillingResult {
  const { isAnonymous, isLoading: authLoading, userId } = useAuth()
  // Only seed from the cache when it belongs to the current user
  const canServeCache = !isAnonymous && projectId !== null && userId !== null && projectBillingCacheUserId === userId
  const [projectBilling, setProjectBilling] = useState<ProjectBillingInfo | null>(() =>
    canServeCache ? (projectBillingCache.get(projectId) ?? null) : null,
  )
  const [isLoading, setIsLoading] = useState(() => !(canServeCache && projectBillingCache.has(projectId)))
  const [error, setError] = useState<Error | null>(null)
  const prevIsAnonymous = useRef(isAnonymous)
  const previousAuthIdentity = useRef({ isAnonymous, userId })
  const latestRequestId = useRef(0)

  const fetchProjectBilling = useCallback(async () => {
    const requestId = ++latestRequestId.current
    if (!projectId) {
      setIsLoading(false)
      return
    }

    try {
      const info = await getProjectBilling(projectId)
      if (requestId !== latestRequestId.current) return
      projectBillingCache.set(projectId, info)
      setProjectBilling(info)
      setError(null)
    } catch (err) {
      if (requestId !== latestRequestId.current) return
      setError(err instanceof Error ? err : new Error('Failed to fetch project billing'))
    } finally {
      if (requestId === latestRequestId.current) setIsLoading(false)
    }
  }, [projectId])

  /* eslint-disable react-hooks/set-state-in-effect -- auth/project changes intentionally synchronize cached billing state. */
  useEffect(() => {
    const previous = previousAuthIdentity.current
    const authChanged = previous.isAnonymous !== isAnonymous || previous.userId !== userId
    if (!authChanged && !isAnonymous) return

    previousAuthIdentity.current = { isAnonymous, userId }
    latestRequestId.current += 1
    projectBillingCache.clear()
    projectBillingCacheUserId = isAnonymous ? null : userId
    setProjectBilling(null)
    setError(null)
    if (isAnonymous) setIsLoading(false)
  }, [isAnonymous, userId])

  // Fetch when projectId changes (skip if auth is still loading or user is anonymous)
  useEffect(() => {
    if (!authLoading) {
      if (isAnonymous) {
        // Anonymous users don't have billing accounts - skip fetch
        setIsLoading(false)
        return
      }
      ensureCacheOwnedBy(userId)
      const cached = projectId ? projectBillingCache.get(projectId) : undefined
      if (cached) {
        // Serve the cached value immediately; revalidate in the background
        setProjectBilling(cached)
        setIsLoading(false)
      } else {
        setIsLoading(true)
      }
      fetchProjectBilling()
    }
  }, [fetchProjectBilling, authLoading, isAnonymous, projectId, userId])

  // Re-fetch when user transitions from anonymous to real account
  useEffect(() => {
    if (prevIsAnonymous.current && !isAnonymous && !authLoading && projectId) {
      console.log('[ProjectBilling] Auth changed from anonymous to real - refreshing billing')
      setIsLoading(true)
      fetchProjectBilling()
    }
    prevIsAnonymous.current = isAnonymous
  }, [isAnonymous, authLoading, projectId, fetchProjectBilling])
  /* eslint-enable react-hooks/set-state-in-effect */

  // Refresh on window focus (skip for anonymous users)
  useEffect(() => {
    const handleFocus = () => {
      if (projectId && !isAnonymous) {
        fetchProjectBilling()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [projectId, fetchProjectBilling, isAnonymous])

  // Refresh when billing state changes externally (dispatched after topup, generation completion, etc.)
  useEffect(() => {
    const handleRefresh = () => {
      if (projectId && !isAnonymous) fetchProjectBilling()
    }
    window.addEventListener('project-billing-refresh', handleRefresh)
    return () => window.removeEventListener('project-billing-refresh', handleRefresh)
  }, [projectId, fetchProjectBilling, isAnonymous])

  return {
    projectBilling: isAnonymous ? null : projectBilling,
    isLoading,
    error,
    refresh: fetchProjectBilling,
  }
}
